"use client";

import { ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function SpecializedPathsToHealing() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const services = [{
    code: "SVC / 01",
    title: "Individual Therapy",
    description: "A safe, non-judgmental space to process depression, anxiety, and trauma at your own pace.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699238/img/individual_therapy_session.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699238/img/individual_therapy_session.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699238/img/individual_therapy_session.png-wvc-sizes",
    features: ["Evidence-based", "Personalized care", "Flexible scheduling"]
  }, {
    code: "SVC / 02",
    title: "Couples & Family",
    description: "Rebuild connection and resolve troubled relationships through guided, empathetic communication.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699238/img/couples_counseling_session.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699238/img/couples_counseling_session.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699238/img/couples_counseling_session.png-wvc-sizes",
    features: ["Conflict resolution", "Improved intimacy", "Family dynamics"]
  }, {
    code: "SVC / 03",
    title: "Telehealth Services",
    description: "Professional counseling from the comfort of your home, available in English, Spanish, and Portuguese.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699238/img/telehealth_session_setup.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699238/img/telehealth_session_setup.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699238/img/telehealth_session_setup.png-wvc-sizes",
    features: ["Secure platform", "Multilingual support", "Day/Night options"]
  }];
  return <section data-section-id="818" id="specialized-paths-to-healing" className="dark relative bg-background py-20 md:py-32 overflow-hidden">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{
      backgroundImage: "radial-gradient(circle, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "24px 24px"
    }} />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-16 lg:mb-24 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-7">
            <div className="font-mono text-xs tracking-[0.2em] uppercase text-foreground/60 mb-6">
              03 / Pathways
            </div>
            <h2 className="font-default font-semibold tracking-tight text-foreground text-[2.5rem] sm:text-5xl lg:text-[4.5rem] leading-[0.95]">
              Finding balance.
              <br />
              <span className="italic font-serif font-normal">Reclaiming you.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pb-3">
            <p className="font-default text-base lg:text-lg text-foreground/70 leading-relaxed mb-6">
              With over 10 years of experience, I provide evidence-based techniques to help you navigate life's challenges, resolve trauma, and build the meaningful life you deserve.
            </p>
            <Link to="/AboutMePage" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.15em] uppercase text-foreground border-b border-foreground pb-1 hover:gap-3 transition-all">
              Learn about my approach
              <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {services.map((service, i) => <div data-index={i} key={i} className={`group border border-foreground/20 bg-card flex flex-col overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:shadow-xl rounded-2xl ${i === 1 ? "md:translate-y-12" : ""}`} style={{
          aspectRatio: "auto"
        }}>
              {}
              <div className="relative w-full overflow-hidden aspect-[4/3]">
                <img src={service.image} data-wvc-srcset={service.srcset} data-wvc-sizes={service.sizes} alt={service.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
              </div>

              {}
              <div className="flex-1 flex flex-col p-8">
                

                <h3 className="font-default text-2xl md:text-[28px] font-semibold tracking-tight text-foreground leading-tight mb-3">
                  {service.title}
                </h3>
                <p className="font-default text-sm text-foreground/70 leading-relaxed mb-8">
                  {service.description}
                </p>

                <ul className="mt-auto flex flex-col sm:flex-row sm:flex-wrap gap-x-5 gap-y-2 pt-6 border-t border-foreground/10">
                  {service.features.map((feature, fi) => <li data-index={fi} key={fi} className="flex items-center gap-2 font-mono text-[11px] tracking-wide uppercase text-foreground/80">
                      <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-lg bg-primary" />
                      {feature}
                    </li>)}
                </ul>
              </div>
            </div>)}
        </div>

        {}
        <div className="mt-20 md:mt-28 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-10 border-t border-foreground/20">
          <p className="font-default text-sm text-foreground/70 max-w-md">
            Ready to take the first step? Contact me today for a free consultation.
          </p>
          <Button asChild className="bg-foreground text-background hover:bg-foreground/90 h-12 px-8 rounded-lg font-mono text-xs tracking-[0.15em] uppercase">
            
          </Button>
        </div>
      </div>
    </section>;
}